import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { History, CheckCircle2, Clock, XCircle, Eye, Filter, AlertCircle } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/_core/hooks/useAuth";

export default function QuestionarioHistorico() {
  usePageTitle("Histórico de Questionários");
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // V82b: Consultor Canal não tem acesso ao módulo de Questionários
  if (user?.role === "channel_consultant") {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
            <h2 className="text-xl font-semibold text-muted-foreground">Acesso Restrito</h2>
            <p className="text-sm text-muted-foreground">Seu perfil não possui acesso ao módulo de Questionários.</p>
            <Button variant="outline" onClick={() => setLocation("/home/canal")}>Voltar ao Início</Button>
          </div>
        </div>
      </div>
    );
  }
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");

  // Queries
  const { data: companies, isLoading: loadingCompanies } = trpc.clients.list.useQuery({});
  const { data: campaigns, isLoading: loadingCampaigns } = trpc.questionnaire.history.listCampaigns.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  // Filtrar campanhas por status
  const filteredCampaigns = campaigns?.filter((c) => {
    if (statusFilter === "ALL") return true;
    return c.status === statusFilter;
  });

  const statusConfig = {
    DRAFT: { label: "Rascunho", variant: "secondary" as const, icon: Clock, color: "text-gray-600" },
    ACTIVE: { label: "Em Andamento", variant: "default" as const, icon: Clock, color: "text-blue-600" },
    CLOSED: { label: "Encerrada (Oficial)", variant: "outline" as const, icon: CheckCircle2, color: "text-green-600" },
  };

  return (
    <div className="container py-8 space-y-6">
      {/* Cabeçalho */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <History className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Histórico de Campanhas</h1>
        </div>
        <p className="text-muted-foreground">
          Rastreabilidade completa e auditável de todas as campanhas de questionário aplicadas
        </p>
      </div>

      {/* Alert educativo */}
      <Alert>
        <AlertDescription>
          <strong>Governança Enterprise:</strong> Resultados históricos são oficiais e imutáveis. Campanhas encerradas não podem ser alteradas.
        </AlertDescription>
      </Alert>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
          <CardDescription>Selecione a empresa e o status para visualizar o histórico</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Empresa</label>
              <Select
                value={selectedCompanyId?.toString() || ""}
                onValueChange={(value) => setSelectedCompanyId(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies?.items?.map((company) => (
                    <SelectItem key={company.id} value={company.id.toString()}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="DRAFT">Rascunho</SelectItem>
                  <SelectItem value="ACTIVE">Em Andamento</SelectItem>
                  <SelectItem value="CLOSED">Encerrada (Oficial)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Listagem de Campanhas */}
      {!selectedCompanyId ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Selecione uma empresa para visualizar o histórico de campanhas
          </CardContent>
        </Card>
      ) : loadingCampaigns ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Carregando histórico...
          </CardContent>
        </Card>
      ) : !filteredCampaigns || filteredCampaigns.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Nenhuma campanha encontrada para os filtros selecionados
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Campanhas ({filteredCampaigns.length})</CardTitle>
            <CardDescription>
              Histórico completo de campanhas aplicadas para {companies?.items?.find((c) => c.id === selectedCompanyId)?.name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>kMin</TableHead>
                  <TableHead>Respostas</TableHead>
                  <TableHead>Snapshot</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead>Encerrado em</TableHead>
                  <TableHead>Criado por</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCampaigns.map((campaign) => {
                  const status = statusConfig[campaign.status];
                  const StatusIcon = status.icon;

                  return (
                    <TableRow key={campaign.id}>
                      <TableCell className="font-medium">#{campaign.id}</TableCell>
                      <TableCell>
                        <Badge variant={status.variant} className="flex items-center gap-1 w-fit">
                          <StatusIcon className={`h-3 w-3 ${status.color}`} />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        v{campaign.templateVersion}
                      </TableCell>
                      <TableCell>{campaign.kMin}</TableCell>
                      <TableCell>
                        <Badge variant={campaign.totalResponses >= campaign.kMin ? "default" : "secondary"}>
                          {campaign.totalResponses}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {campaign.hasSnapshot ? (
                          <Badge variant="default" className="bg-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Oficial
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="h-3 w-3 mr-1" />
                            Não disponível
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(campaign.createdAt).toLocaleDateString("pt-BR")}
                      </TableCell>
                      <TableCell className="text-sm">
                        {campaign.closedAt ? new Date(campaign.closedAt).toLocaleDateString("pt-BR") : "-"}
                      </TableCell>
                      <TableCell className="text-sm">{campaign.createdByUserName || "-"}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setLocation(`/questionarios/historico/${campaign.id}`)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Ver Detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
